import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Rocket, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { APP } from "@/constants/testIds";
import DeepDiveDrawer from "@/components/DeepDiveDrawer";

/**
 * Public read-only page for a shared idea + deep-dive snapshot.
 * No auth required. Uses the DeepDiveDrawer component in "always open" mode
 * so shared visitors see the same layout as authenticated users.
 */
export default function SharedIdea() {
  const { shareId } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/share/${shareId}`);
        if (!cancelled) setState({ loading: false, error: null, data: res.data });
      } catch (err) {
        if (!cancelled) {
          const msg =
            err?.response?.status === 404
              ? "This shared idea was not found or has been removed."
              : "We couldn't load this shared idea. Please try again later.";
          setState({ loading: false, error: msg, data: null });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  // Update the browser tab title once the shared idea is loaded.
  useEffect(() => {
    const idea = state.data?.idea;
    if (!idea) return;
    const previous = document.title;
    document.title = `${idea.title} · Trending/Ideas`;
    return () => {
      document.title = previous;
    };
  }, [state.data]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[color:var(--bg)] text-[color:var(--fg)] flex flex-col items-center justify-center p-6">
        <Loader2 className="animate-spin" size={32} />
        <div className="mt-4 font-mono-tight uppercase tracking-widest text-xs text-[color:var(--muted)]">
          Loading shared idea…
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-[color:var(--bg)] text-[color:var(--fg)] flex flex-col items-center justify-center p-6">
        <AlertTriangle size={36} className="text-[color:var(--hot)] mb-4" />
        <div className="font-display text-3xl leading-tight text-center max-w-md">
          {state.error}
        </div>
        <Link
          to="/"
          className="mt-8 border border-[color:var(--fg)] px-5 py-3 font-mono-tight uppercase tracking-widest text-xs hover:bg-[color:var(--fg)] hover:text-[color:var(--bg)] transition-colors"
        >
          Back to trending ideas
        </Link>
      </div>
    );
  }

  const { idea, dive, author_country: authorCountry } = state.data;

  return (
    <div className="min-h-screen bg-[color:var(--bg)] text-[color:var(--fg)]" data-testid={APP.sharedPage}>
      {/* Header banner — invites visitor back to the app */}
      <header className="border-b border-[color:var(--fg)] p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <Rocket size={18} />
          <span className="font-display text-lg tracking-tight">Trending/Ideas</span>
        </Link>
        <div className="flex items-center gap-3">
          <div className="hidden md:block text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)]">
            shared read-only · {authorCountry || "Global"}
          </div>
          <Link
            to="/auth"
            data-testid={APP.sharedPageCta}
            className="border border-[color:var(--fg)] bg-[color:var(--fg)] text-[color:var(--bg)] px-4 py-2 font-mono-tight uppercase tracking-widest text-xs hover:bg-[color:var(--accent)] hover:border-[color:var(--accent)] hover:text-[color:var(--bg)] transition-colors flex items-center gap-2"
          >
            Generate your own <ArrowRight size={12} />
          </Link>
        </div>
      </header>

      {/* Idea card summary */}
      <section className="p-6 md:p-8 border-b border-[color:var(--line)]">
        <div className="text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)]">
          {idea.category}
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mt-2">
          {idea.title}
        </h1>
        <p className="text-[color:var(--muted)] italic mt-3 text-sm md:text-base">{idea.tagline}</p>
        <p className="text-sm md:text-base mt-5 leading-relaxed max-w-3xl">{idea.description}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {(idea.tags || []).slice(0, 6).map((t) => (
            <span key={t} className="tag">
              {t}
            </span>
          ))}
        </div>
      </section>

      {/* Full deep-dive rendered by the drawer in inline read-only mode */}
      <DeepDiveDrawer
        open
        onOpenChange={() => {}}
        idea={idea}
        dive={dive}
        loading={false}
        error={null}
        inline
      />
    </div>
  );
}
